import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Search, Edit, Trash2, Eye, X } from 'lucide-react';
import { toast } from 'react-hot-toast';
import api from '../services/api';
import ConfirmDeleteModal from '../components/ConfirmDeleteModal';

export default function Clientes() {
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingCliente, setEditingCliente] = useState(null);
  const [viewingCliente, setViewingCliente] = useState(null);
  const [deletingCliente, setDeletingCliente] = useState(null);
  const queryClient = useQueryClient();

  const { data: clientes, isLoading } = useQuery({
    queryKey: ['clientes'],
    queryFn: async () => {
      const response = await api.get('/clientes');
      return response.data;
    },
  });

  const createMutation = useMutation({
    mutationFn: (data) => api.post('/clientes', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clientes'] });
      toast.success('Cliente creado exitosamente');
      setShowModal(false);
    },
    onError: () => toast.error('Error al crear cliente'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => api.patch(`/clientes/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clientes'] });
      toast.success('Cliente actualizado exitosamente');
      setShowModal(false);
      setEditingCliente(null);
    },
    onError: () => toast.error('Error al actualizar cliente'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => api.delete(`/clientes/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clientes'] });
      toast.success('Cliente eliminado');
      setDeletingCliente(null);
    },
    onError: () => toast.error('Error al eliminar cliente'),
  });

  const filteredClientes = clientes?.filter((c) =>
    `${c.nombre} ${c.apellido} ${c.dniCuit}`.toLowerCase().includes(search.toLowerCase())
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const data = Object.fromEntries(formData);

    if (editingCliente) {
      updateMutation.mutate({ id: editingCliente.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const openEditModal = (cliente) => {
    setEditingCliente(cliente);
    setShowModal(true);
  };

  const openCreateModal = () => {
    setEditingCliente(null);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingCliente(null);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900">Clientes</h1>
        <button
          onClick={openCreateModal}
          className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          <Plus size={20} />
          <span>Nuevo Cliente</span>
        </button>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
        <input
          type="text"
          placeholder="Buscar cliente..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
        />
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nombre</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">DNI/CUIT</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Telefono</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Localidad</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filteredClientes?.map((cliente) => (
              <tr key={cliente.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 font-medium text-gray-900">
                  {cliente.nombre} {cliente.apellido}
                </td>
                <td className="px-6 py-4 text-gray-600">{cliente.dniCuit}</td>
                <td className="px-6 py-4 text-gray-600">{cliente.telefono}</td>
                <td className="px-6 py-4 text-gray-600">{cliente.localidad}</td>
                <td className="px-6 py-4">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    cliente.activo ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {cliente.activo ? 'Activo' : 'Inactivo'}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setViewingCliente(cliente)}
                      className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                    >
                      <Eye size={16} />
                    </button>
                    <button
                      onClick={() => openEditModal(cliente)}
                      className="p-1 text-yellow-600 hover:bg-yellow-50 rounded"
                    >
                      <Edit size={16} />
                    </button>
                    <button
                      onClick={() => setDeletingCliente(cliente)}
                      className="p-1 text-red-600 hover:bg-red-50 rounded"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal Ver Cliente */}
      {viewingCliente && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-8 w-full max-w-lg">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Detalle del Cliente</h2>
              <button onClick={() => setViewingCliente(null)} className="p-1 hover:bg-gray-100 rounded">
                <X size={20} />
              </button>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between py-2 border-b">
                <span className="text-gray-600">Nombre completo</span>
                <span className="font-medium">{viewingCliente.nombre} {viewingCliente.apellido}</span>
              </div>
              <div className="flex justify-between py-2 border-b">
                <span className="text-gray-600">DNI/CUIT</span>
                <span className="font-medium">{viewingCliente.dniCuit}</span>
              </div>
              <div className="flex justify-between py-2 border-b">
                <span className="text-gray-600">Telefono</span>
                <span className="font-medium">{viewingCliente.telefono || '-'}</span>
              </div>
              <div className="flex justify-between py-2 border-b">
                <span className="text-gray-600">Email</span>
                <span className="font-medium">{viewingCliente.email || '-'}</span>
              </div>
              <div className="flex justify-between py-2 border-b">
                <span className="text-gray-600">Direccion</span>
                <span className="font-medium">{viewingCliente.direccion}</span>
              </div>
              <div className="flex justify-between py-2 border-b">
                <span className="text-gray-600">Localidad</span>
                <span className="font-medium">{viewingCliente.localidad || '-'}</span>
              </div>
              <div className="flex justify-between py-2 border-b">
                <span className="text-gray-600">Provincia</span>
                <span className="font-medium">{viewingCliente.provincia}</span>
              </div>
              <div className="flex justify-between py-2 border-b">
                <span className="text-gray-600">Condicion IVA</span>
                <span className="font-medium">{viewingCliente.condicionIva}</span>
              </div>
              <div className="flex justify-between py-2 border-b">
                <span className="text-gray-600">Nodo</span>
                <span className="font-medium">{viewingCliente.nodo || '-'}</span>
              </div>
              <div className="flex justify-between py-2 border-b">
                <span className="text-gray-600">Fecha de alta</span>
                <span className="font-medium">{new Date(viewingCliente.fechaAlta).toLocaleDateString('es-AR')}</span>
              </div>
              <div className="flex justify-between py-2">
                <span className="text-gray-600">Estado</span>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  viewingCliente.activo ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  {viewingCliente.activo ? 'Activo' : 'Inactivo'}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Crear/Editar Cliente */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-8 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              {editingCliente ? 'Editar Cliente' : 'Nuevo Cliente'}
            </h2>
            <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
                <input name="nombre" defaultValue={editingCliente?.nombre} required className="w-full px-3 py-2 border rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Apellido</label>
                <input name="apellido" defaultValue={editingCliente?.apellido} required className="w-full px-3 py-2 border rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">DNI/CUIT</label>
                <input name="dniCuit" defaultValue={editingCliente?.dniCuit} required className="w-full px-3 py-2 border rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Telefono</label>
                <input name="telefono" defaultValue={editingCliente?.telefono} className="w-full px-3 py-2 border rounded-lg" />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input name="email" type="email" defaultValue={editingCliente?.email} className="w-full px-3 py-2 border rounded-lg" />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Direccion</label>
                <input name="direccion" defaultValue={editingCliente?.direccion} required className="w-full px-3 py-2 border rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Localidad</label>
                <input name="localidad" defaultValue={editingCliente?.localidad} className="w-full px-3 py-2 border rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Provincia</label>
                <input name="provincia" defaultValue={editingCliente?.provincia || 'Buenos Aires'} className="w-full px-3 py-2 border rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Condicion IVA</label>
                <select name="condicionIva" defaultValue={editingCliente?.condicionIva || 'Consumidor Final'} className="w-full px-3 py-2 border rounded-lg">
                  <option>Consumidor Final</option>
                  <option>Responsable Inscripto</option>
                  <option>Monotributo</option>
                  <option>Exento</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nodo</label>
                <input name="nodo" defaultValue={editingCliente?.nodo} className="w-full px-3 py-2 border rounded-lg" />
              </div>
              <div className="col-span-2 flex justify-end space-x-3 mt-4">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
                >
                  {createMutation.isPending || updateMutation.isPending
                    ? 'Guardando...'
                    : editingCliente ? 'Actualizar' : 'Guardar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Confirmar Eliminacion */}
      <ConfirmDeleteModal
        open={!!deletingCliente}
        title="Eliminar Cliente"
        message={
          deletingCliente ? (
            <>¿Estás seguro de eliminar a <strong>{deletingCliente.nombre} {deletingCliente.apellido}</strong>?</>
          ) : ''
        }
        loading={deleteMutation.isPending}
        onConfirm={() => deleteMutation.mutate(deletingCliente.id)}
        onCancel={() => setDeletingCliente(null)}
      />
    </div>
  );
}
